
import { FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { GlassCard, GlassCardContent } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { PPPCPermissions, PPPCService, PPPC_SERVICE_LABELS } from "@/types/application";
import { UseFormReturn } from "react-hook-form";

interface PrivacyPermissionItemProps {
  index: number;
  form: UseFormReturn<any>;
  onRemove: () => void;
  availableServices: { value: string; label: string }[];
}

export const PrivacyPermissionItem = ({
  index,
  form,
  onRemove,
  availableServices
}: PrivacyPermissionItemProps) => {
  // Check if the service value exists before using it
  const serviceValue = form.watch(`pppcSettings.privacyPermissions.${index}.serviceIdentifier`) as PPPCService;
  
  return (
    <GlassCard variant="subtle" className="border-dashed">
      <GlassCardContent className="pt-6 relative">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-8 w-8 p-0"
          onClick={onRemove}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Remove</span>
        </Button>
      
        <div className="grid gap-4">
          <FormField
            control={form.control}
            name={`pppcSettings.privacyPermissions.${index}.serviceIdentifier`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={field.value}>
                      {PPPC_SERVICE_LABELS[field.value as PPPCService] || field.value}
                    </SelectItem>
                    {availableServices.map(service => (
                      <SelectItem key={service.value} value={service.value}>
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  The system service or data this permission controls
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`pppcSettings.privacyPermissions.${index}.allowed`}
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox 
                    checked={field.value} 
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Allow Access</FormLabel>
                  <FormDescription>
                    Allow this application to access this service
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>
        
        <div className="mt-4">
          {serviceValue && (
            <Badge variant="outline" className="bg-slate-50">
              {PPPC_SERVICE_LABELS[serviceValue] || serviceValue}
            </Badge>
          )}
        </div>
      </GlassCardContent>
    </GlassCard>
  );
};
